<template>
  <div class="otp-verify-card">
    <!-- ── Verified state ── -->
    <div v-if="original.isPhoneVerified && !meta.requestChange" class="otp-verified">
      <span class="otp-verified__badge">
        <svg viewBox="0 0 16 16" fill="currentColor"><path d="M13.485 1.431a1.473 1.473 0 0 1 2.104 2.062l-7.84 9.801a1.473 1.473 0 0 1-2.12.04L.431 8.138a1.473 1.473 0 0 1 2.084-2.083l4.111 4.112 6.86-8.736z"/></svg>
      </span>
      <div class="otp-verified__info">
        <span class="otp-verified__label">Mobile verified</span>
        <span class="otp-verified__value">{{ phone }}</span>
      </div>
      <button class="otp-verified__change btn-fm btn-fm-outline" type="button"
              @click.prevent="meta.requestChange = true">
        Change
      </button>
    </div>

    <!-- ── Unverified / change state ── -->
    <div v-else class="d-flex flex-column" style="gap: 1rem;">
      <FormGroup :error-container="errorContainer" label="Mobile Number" name="phone" :floating="true">
        <input :id="id" ref="input" v-model="phone"
               :readonly="meta.loading || meta.codeSent"
               class="form-control"
               name="phone"
               placeholder=" "
               required
               type="text"
               @keyup.enter="sendCode"/>
      </FormGroup>

      <TimeCountdown
        v-if="meta.preventResend"
        :disabled="meta.loading"
        :from="60"
        class="fm-resend-hint"
        request-change-text="Change Phone"
        @requestChange="onRequestPhoneChange"
        @resend="sendCode"
      />

      <FormGroup v-if="meta.codeSent" :error-container="errorContainer" label="Verification Code" name="code" :floating="true">
        <input :id="codeId" v-model="form.code" :readonly="meta.loading"
               class="form-control otp-code-input"
               name="code" required type="text"
               maxlength="6" placeholder=" "
               autocomplete="one-time-code"/>
      </FormGroup>

      <div class="d-flex gap-2 flex-wrap">
        <button v-if="original.isPhoneVerified" :disabled="meta.loading" class="btn-fm btn-fm-outline" type="button"
                @click="requestChange(false)">
          Cancel
        </button>
        <button v-if="!meta.codeSent" :disabled="meta.loading || !isContactChanged"
                class="ml-2 btn-fm btn-fm-primary" type="button" @click="sendCode">
          <SpinKit v-if="meta.loading" :loading="true"/>
          <span v-else>Send Code</span>
        </button>
        <button v-if="meta.codeSent" :disabled="meta.loading"
                class="btn-fm btn-fm-primary" type="button" @click="verifyCode">
          <SpinKit v-if="meta.loading" :loading="true"/>
          <span v-else>Verify Code</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TimeCountdown from '@modules/guest/new-applicant/components/steps/ContactVerification/components/TimeCountdown';
import SpinKit from '@components/common/SpinKit';
import FormGroup from '@modules/shared/components/form/FormGroup';
import ServerErrorMixin from '@modules/shared/mixins/ServerErrorMixin';
import store from '@modules/guest/new-applicant/store/store';
import {NEW_APPLICATION} from '@store/constants';
import {
  sendContactChangeVerificationOTPCode,
  verifyContactChangeVerificationOTPCode,
} from '@modules/guest/new-applicant/NewApplicationService';
import {validateMobileNumber} from '@modules/guest/new-applicant/components/steps/MoreAboutYourself/formValidator';
import {Str} from '@/utilities/random';

export default {
  name: 'PhoneVerification',
  props: ['errorContainer'],
  mixins: [ServerErrorMixin],
  components: {TimeCountdown, SpinKit, FormGroup},
  computed: {
    code: () => store.state[NEW_APPLICATION.MODULE_NAME].code,
    original: () => {
      const original = store.state[NEW_APPLICATION.MODULE_NAME].cache;
      return { phone: original?.phone ?? '', isPhoneVerified: !!original?.phone_verified_at };
    },
    isContactChanged() {
      return !this.original.isPhoneVerified || !this.phone.trim() || this.original.phone.trim() !== this.phone.trim();
    },
  },
  data() {
    return {
      id: Str.random({length: 6, unique: true}),
      codeId: Str.random({length: 6, unique: true}),
      phone: store.state[NEW_APPLICATION.MODULE_NAME].cache?.phone ?? '',
      form: { code: '' },
      meta: { loading: false, codeSent: false, preventResend: false, requestChange: false },
    };
  },
  watch: {
    ['original.isPhoneVerified'](verified) {
      if (!verified) return;
      this.phone = this.original.phone;
    },
    errorContainer: {
      handler(value) { if (value) this.registerErrorContainer(value); },
      immediate: true,
    },
  },
  methods: {
    validate() {
      this.setErrorByKey('phone');
      if (!this.$refs.input?.validity.valid) this.setErrorByKey('phone', 'Phone is invalid');
      else this.setErrorByKey('phone', validateMobileNumber({mobileNumber: this.phone}));
    },
    async sendCode() {
      if (this.meta.loading) return;
      if (!this.isContactChanged) { this.requestChange(false); return; }
      this.validate();
      if (this.hasError('phone')) return;
      this.meta.loading = true;
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, hasChanged}} = await sendContactChangeVerificationOTPCode(this.code, {phone: this.phone});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
        this.meta.codeSent = true;
        this.meta.preventResend = true;
        if (!hasChanged) this.requestChange(false);
      } catch (e) { await this.handleServerError(e); }
      finally { this.meta.loading = false; store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    async verifyCode() {
      const error = this.form.code.trim().length === 0 ? 'Verification code is required' : null;
      this.setErrorByKey('code', error);
      if (this.hasError('code')) return;
      this.meta.loading = true;
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp}} = await verifyContactChangeVerificationOTPCode(this.code, {phone: this.phone, code: this.form.code});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
        this.meta.requestChange = false;
        this.meta.codeSent = false;
        this.meta.preventResend = false;
        this.form.code = '';
      } catch (e) { await this.handleServerError(e); throw e; }
      finally { this.meta.loading = false; store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    onRequestPhoneChange() { this.meta.codeSent = false; this.meta.preventResend = false; },
    requestChange(change) {
      this.meta.requestChange = change;
      if (!change && this.original.isPhoneVerified) {
        this.phone = this.original.phone;
        this.meta.requestChange = false;
        this.meta.codeSent = false;
        this.meta.preventResend = false;
        this.form.code = '';
      }
    },
  },
};
</script>
